import { SelectItem } from 'primeng/api';
import { WorkbookJobTypes } from '../../../models/workbookJobTypes';
import { ShiftSummary, ShiftSummaryPartCTestStatus, ShiftSummaryProfile } from '../../../services/nswag/nswagclient';

export const PART_C_TEST_STATUS_VALUES = {
  yes: ShiftSummaryPartCTestStatus.Yes,
  no: ShiftSummaryPartCTestStatus.No,
  noLcmUsePlanned: ShiftSummaryPartCTestStatus.NoLcmUsePlanned
} as const;

export type ShiftSummaryPartCTestStatusSelection =
  typeof PART_C_TEST_STATUS_VALUES[keyof typeof PART_C_TEST_STATUS_VALUES];

export const SHIFT_SUMMARY_PROFILE_VALUES = {
  hydroLyft: ShiftSummaryProfile.HydroLyft,
  drilloutWashout: ShiftSummaryProfile.DrilloutWashout
} as const;

export interface ShiftSummaryCreateFormState {
  jobId: string;
  summaryProfile: ShiftSummaryProfile;
  shiftStartUtc: Date | null;
  shiftEndUtc: Date | null;
  dayNight: string;
  selectedCrewMemberUserIds: string[];
  crewMemberOptions: ShiftSummaryCrewMemberOption[];
  currentStatus: string;
  safetyTopicsDiscussed: string;
  anySafetyIncidents: boolean;
  safetyIncidentDetails: string;
  safetyIncidents: string[];
  jobStatus: string;
  customerFeedback: string;
  mechanicalNeeds: string;
  nextPlannedWork: string;
  dataComponentsWorking: boolean;
  dataComponentsContext: string;
  fluidType: string;
  currentFluidSystem: string;
  foamStabilityMinutes: number | null;
  sandSuspensionMinutes: number | null;
  partCTestStatus: ShiftSummaryPartCTestStatusSelection | '';
  gramsToPass: number | null;
  waterLeakOff: number | null;
  iscavGallonsConsumed: number | null;
  procideGallonsConsumed: number | null;
  fuelProvidedByCustomer: boolean;
}

export interface ShiftSummaryPersonnelOptionsInput {
  selectedCrewMemberUserIds: string[];
  crewMemberPersonnelOptions: ShiftSummaryCrewMemberOption[];
}

export interface ShiftSummaryPersonnelOptionsResult {
  crewMemberOptions: SelectItem[];
}

export interface ShiftSummaryCrewMemberOption {
  userId: string;
  userName: string;
}

export interface ShiftSummaryTimeWarningState {
  shiftStartUtc: Date | null;
  shiftEndUtc: Date | null;
  dayNight: string;
}

export interface ShiftShortcutOption {
  label: string;
  shiftStartUtc: Date;
  shiftEndUtc: Date;
}

const THIRTY_MINUTES_MS = 30 * 60 * 1000;
const SHIFT_SHORTCUT_DURATION_HOURS = 12;
const SHIFT_SHORTCUT_COUNT_PER_SIDE = 6;

export function buildShiftShortcutOptions(
  now: Date,
  formatShiftShortcutDate: (date: Date) => string
): ShiftShortcutOption[] {
  const shiftEndsBeforeNow = getShiftEndsBefore(now, SHIFT_SHORTCUT_COUNT_PER_SIDE);
  const shiftEndsAfterNow = getShiftEndsAfter(now, SHIFT_SHORTCUT_COUNT_PER_SIDE);

  return [...shiftEndsBeforeNow, ...shiftEndsAfterNow].map(shiftEnd => {
    const shiftStart = addHours(shiftEnd, -SHIFT_SHORTCUT_DURATION_HOURS);

    return {
      shiftStartUtc: shiftStart,
      shiftEndUtc: shiftEnd,
      label: `${formatShiftShortcutDate(shiftStart)} - ${formatShiftShortcutDate(shiftEnd)}`
    };
  });
}

export function buildShiftSummaryPayload(state: ShiftSummaryCreateFormState): ShiftSummary {
  const summary = new ShiftSummary();
  const supportsFoamValues = supportsFoamMetrics(state.summaryProfile);
  const supportsCurrentFluidSystemValue = supportsCurrentFluidSystem(state.summaryProfile);
  const hasPartCDetails = showsPartCDetails(state.partCTestStatus);
  summary.jobId = state.jobId;
  summary.summaryProfile = state.summaryProfile;
  summary.shiftStartUtc = state.shiftStartUtc!;
  summary.shiftEndUtc = state.shiftEndUtc!;
  summary.dayNight = state.dayNight;
  summary.crewMemberUserIds = normalizeSelectedCrewMemberUserIds(state.selectedCrewMemberUserIds);
  summary.crewMembers = buildCrewMemberDisplayText(summary.crewMemberUserIds, state.crewMemberOptions);
  summary.currentStatus = state.currentStatus;
  summary.safetyTopicsDiscussed = state.safetyTopicsDiscussed.trim() || undefined;
  summary.anySafetyIncidents = state.anySafetyIncidents;
  summary.safetyIncidentDetails = state.anySafetyIncidents === true
    ? state.safetyIncidentDetails.trim() || undefined
    : undefined;
  summary.safetyIncidents = state.anySafetyIncidents === true ? state.safetyIncidents : [];
  summary.jobStatus = state.jobStatus;
  summary.customerFeedback = state.customerFeedback;
  summary.mechanicalNeeds = state.mechanicalNeeds;
  summary.nextPlannedWork = state.nextPlannedWork;
  summary.dataComponentsWorking = state.dataComponentsWorking;
  summary.dataComponentsContext = state.dataComponentsWorking === false
    ? state.dataComponentsContext.trim() || undefined
    : undefined;
  summary.fluidType = state.fluidType || undefined;
  summary.currentFluidSystem = supportsCurrentFluidSystemValue
    ? state.currentFluidSystem || undefined
    : undefined;
  summary.foamStabilityMinutes = supportsFoamValues ? state.foamStabilityMinutes ?? undefined : undefined;
  summary.sandSuspensionMinutes = supportsFoamValues ? state.sandSuspensionMinutes ?? undefined : undefined;
  summary.partCTestStatus = state.partCTestStatus === '' ? undefined : state.partCTestStatus;
  summary.gramsToPass = hasPartCDetails ? state.gramsToPass ?? undefined : undefined;
  summary.waterLeakOff = hasPartCDetails ? state.waterLeakOff ?? undefined : undefined;
  summary.iscavGallonsConsumed = state.iscavGallonsConsumed ?? undefined;
  summary.procideGallonsConsumed = state.procideGallonsConsumed ?? undefined;
  summary.fuelProvidedByCustomer = state.fuelProvidedByCustomer;
  return summary;
}

export function validateShiftSummaryForSave(state: Pick<ShiftSummaryCreateFormState, 'shiftStartUtc' | 'shiftEndUtc'>): string | null {
  if (!state.shiftStartUtc || !state.shiftEndUtc) {
    return 'Shift Start and Shift End are required to save a draft.';
  }

  if (state.shiftEndUtc <= state.shiftStartUtc) {
    return 'Shift End must be after Shift Start.';
  }

  return null;
}

export function validateShiftSummaryForSubmit(state: ShiftSummaryCreateFormState): string | null {
  const saveError = validateShiftSummaryForSave(state);
  if (saveError) {
    return saveError;
  }

  if (!state.selectedCrewMemberUserIds.length || !state.currentStatus || !state.jobStatus) {
    return 'Crew Members, Current Status, and Job Status are required to submit.';
  }

  if (!state.fluidType) {
    return 'Fluid Type is required to submit.';
  }

  if (supportsCurrentFluidSystem(state.summaryProfile) && !state.currentFluidSystem) {
    return 'Recirc/Base Fluid and Current Fluid System are required to submit.';
  }

  if (requiresFoamAndSandMetrics(state.currentFluidSystem)
    && (state.foamStabilityMinutes == null || state.sandSuspensionMinutes == null)) {
    return 'Foam Stability Time and Sand Suspension Time are required when Current Fluid System is HydroLyft or TurboLyft.';
  }

  if (state.anySafetyIncidents === true) {
    if (!state.safetyIncidentDetails.trim()) {
      return 'Safety Incident Details are required when safety incidents are marked Yes.';
    }

    if (!state.safetyIncidents.length) {
      return 'Incidents are required when safety incidents are marked Yes.';
    }
  }

  if (!state.customerFeedback.trim()) {
    return 'Last Discussion with Customer is required to submit.';
  }

  if (state.dataComponentsWorking === false && !state.dataComponentsContext.trim()) {
    return 'Data Components Context is required when data components are not working.';
  }

  if (showsPartCDetails(state.partCTestStatus) && (state.gramsToPass == null || state.waterLeakOff == null)) {
    return 'Grams to Pass and Water Leak Off are required when Part C Test Status is Yes or No.';
  }

  return null;
}

export function buildPersonnelOptions(input: ShiftSummaryPersonnelOptionsInput): ShiftSummaryPersonnelOptionsResult {
  return {
    crewMemberOptions: toCrewMemberSelectItems(input.crewMemberPersonnelOptions, input.selectedCrewMemberUserIds)
  };
}

export function parseCrewMembers(crewMembers: string | undefined): string[] {
  return (crewMembers ?? '')
    .split(',')
    .map(value => value.trim())
    .filter(value => !!value);
}

export function resolveShiftSummaryProfile(workbookType: string | undefined | null): ShiftSummaryProfile {
  switch (workbookType) {
    case WorkbookJobTypes.tcpFracDrillout:
    case WorkbookJobTypes.fESCODrillout:
    case WorkbookJobTypes.fRCSDilloutWashout:
    case WorkbookJobTypes.eQTDrilloutWashout:
      return ShiftSummaryProfile.DrilloutWashout;
    case WorkbookJobTypes.hydroLyftJob:
    default:
      return ShiftSummaryProfile.HydroLyft;
  }
}

export function isDrilloutWashoutProfile(summaryProfile: ShiftSummaryProfile | undefined | null): boolean {
  return summaryProfile === ShiftSummaryProfile.DrilloutWashout;
}

export function supportsCurrentFluidSystem(summaryProfile: ShiftSummaryProfile | undefined | null): boolean {
  return !isDrilloutWashoutProfile(summaryProfile);
}

export function supportsFoamMetrics(summaryProfile: ShiftSummaryProfile | undefined | null): boolean {
  return !isDrilloutWashoutProfile(summaryProfile);
}

export function requiresFoamAndSandMetrics(currentFluidSystem: string | undefined | null): boolean {
  const normalizedFluidSystem = currentFluidSystem?.trim().toLowerCase();
  return normalizedFluidSystem === 'hydrolyft' || normalizedFluidSystem === 'turbolyft';
}

export function getShiftTimeWarningMessages(state: ShiftSummaryTimeWarningState): string[] {
  if (!state.shiftStartUtc || !state.shiftEndUtc || state.shiftEndUtc <= state.shiftStartUtc) {
    return [];
  }

  const messages: string[] = [];
  const totalHours = (state.shiftEndUtc.getTime() - state.shiftStartUtc.getTime()) / (60 * 60 * 1000);

  if (totalHours < 3) {
    messages.push('The selected shift is less than 3 hours.');
  } else if (totalHours > 13) {
    messages.push('The selected shift is more than 13 hours.');
  }

  const includesMidnight = shiftIncludesLocalMidnight(state.shiftStartUtc, state.shiftEndUtc);
  const normalizedDayNight = state.dayNight?.trim().toLowerCase();

  if (normalizedDayNight === 'day' && includesMidnight) {
    messages.push('The selected times include midnight but the shift is marked Day.');
  }

  if (normalizedDayNight === 'night' && !includesMidnight) {
    messages.push('The selected times do not include midnight but the shift is marked Night.');
  }

  return messages;
}

export function getFluidTypeOptions(summaryProfile: ShiftSummaryProfile | undefined | null): SelectItem[] {
  return isDrilloutWashoutProfile(summaryProfile)
    ? [
        { label: 'Recirc', value: 'Recirc' },
        { label: 'Fresh Water', value: 'Fresh Water' }
      ]
    : [
        { label: 'Recirc', value: 'Recirc' },
        { label: 'Base', value: 'Base' }
      ];
}

export function getFluidTypeLabel(summaryProfile: ShiftSummaryProfile | undefined | null): string {
  return isDrilloutWashoutProfile(summaryProfile)
    ? 'Are you on Recirc or Fresh Water?'
    : 'Are you on Recirc or Base fluid?';
}

export function showsPartCDetails(partCTestStatus: ShiftSummaryPartCTestStatus | '' | undefined | null): boolean {
  return partCTestStatus === ShiftSummaryPartCTestStatus.Yes
    || partCTestStatus === ShiftSummaryPartCTestStatus.No;
}

export function formatPartCTestStatus(partCTestStatus: ShiftSummaryPartCTestStatus | '' | undefined | null): string {
  switch (partCTestStatus) {
    case ShiftSummaryPartCTestStatus.Yes:
      return 'Yes';
    case ShiftSummaryPartCTestStatus.No:
      return 'No';
    case ShiftSummaryPartCTestStatus.NoLcmUsePlanned:
      return 'No LCM Use Planned';
    default:
      return 'N/A';
  }
}

function normalizeSelectedCrewMemberUserIds(userIds: string[] = []): string[] {
  return Array.from(new Set(
    userIds
      .map(userId => userId?.trim())
      .filter((userId): userId is string => !!userId)
  ));
}

function buildCrewMemberDisplayText(userIds: string[] = [], options: ShiftSummaryCrewMemberOption[] = []): string {
  const displayNamesById = new Map(
    options
      .filter(option => !!option.userId && !!option.userName)
      .map(option => [option.userId, option.userName.trim()])
  );

  return userIds
    .map(userId => displayNamesById.get(userId) ?? userId)
    .join(', ');
}

function toCrewMemberSelectItems(options: ShiftSummaryCrewMemberOption[], selectedUserIds: string[] = []): SelectItem[] {
  const byUserId = new Map<string, SelectItem>();

  for (const option of options) {
    const userId = option.userId?.trim();
    const userName = option.userName?.trim();
    if (!userId || !userName || byUserId.has(userId)) {
      continue;
    }

    byUserId.set(userId, { label: userName, value: userId });
  }

  for (const userId of selectedUserIds) {
    const normalizedUserId = userId?.trim();
    if (normalizedUserId && !byUserId.has(normalizedUserId)) {
      byUserId.set(normalizedUserId, { label: normalizedUserId, value: normalizedUserId });
    }
  }

  return Array.from(byUserId.values());
}

export function isImageAttachment(contentType: string | undefined | null, fileName: string | undefined | null): boolean {
  const normalizedType = (contentType ?? '').trim().toLowerCase();
  if (normalizedType.startsWith('image/')) {
    return true;
  }

  if (normalizedType && normalizedType !== 'application/octet-stream') {
    return false;
  }

  const normalizedName = (fileName ?? '').trim().toLowerCase();
  return ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp', '.svg', '.tif', '.tiff', '.heic', '.heif']
    .some(extension => normalizedName.endsWith(extension));
}

export function normalizeSelectedFiles(files: File[] | ArrayLike<File> | null | undefined): File[] {
  if (Array.isArray(files)) {
    return files.filter((file): file is File => !!file);
  }

  if (files && typeof files === 'object' && 'length' in files && typeof files.length === 'number') {
    console.warn('[ShiftSummary] File upload select event emitted a non-array files payload.', files);
    return Array.from(files).filter((file): file is File => !!file);
  }

  if (files != null) {
    console.warn('[ShiftSummary] File upload select event emitted an unexpected files payload.', files);
  }

  return [];
}

function getShiftEndsBefore(now: Date, count: number): Date[] {
  const currentHalfHour = floorToThirtyMinutes(now);
  const firstShiftEnd = currentHalfHour.getTime() < now.getTime()
    ? currentHalfHour
    : addMinutes(currentHalfHour, -30);

  return Array.from({ length: count }, (_, index) => addMinutes(firstShiftEnd, -30 * index));
}

function getShiftEndsAfter(now: Date, count: number): Date[] {
  const nextHalfHour = ceilToThirtyMinutes(now);
  const firstShiftEnd = nextHalfHour.getTime() > now.getTime()
    ? nextHalfHour
    : addMinutes(nextHalfHour, 30);

  return Array.from({ length: count }, (_, index) => addMinutes(firstShiftEnd, 30 * index));
}

function floorToThirtyMinutes(date: Date): Date {
  return new Date(Math.floor(date.getTime() / THIRTY_MINUTES_MS) * THIRTY_MINUTES_MS);
}

function ceilToThirtyMinutes(date: Date): Date {
  const floored = floorToThirtyMinutes(date);
  if (floored.getTime() === date.getTime()) {
    return floored;
  }

  return addMinutes(floored, 30);
}

function addHours(date: Date, hours: number): Date {
  return new Date(date.getTime() + (hours * 60 * 60 * 1000));
}

function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + (minutes * 60 * 1000));
}

function shiftIncludesLocalMidnight(shiftStart: Date, shiftEnd: Date): boolean {
  const firstMidnight = new Date(shiftStart);
  firstMidnight.setHours(0, 0, 0, 0);

  if (firstMidnight.getTime() < shiftStart.getTime()) {
    firstMidnight.setDate(firstMidnight.getDate() + 1);
  }

  return firstMidnight.getTime() <= shiftEnd.getTime();
}
